import React, { useContext, useEffect } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useHistory, useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import ContactsRoundedIcon from "@mui/icons-material/ContactsRounded";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CollectionsBookmarkRoundedIcon from "@mui/icons-material/CollectionsBookmarkRounded";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import FolderSharedRoundedIcon from "@mui/icons-material/FolderSharedRounded";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

import { DRAWER_WIDTH, stringToHslColor } from "../tools";
import AppContext from "../appContext";
import useAccountSettings from "../ManageAccount/useAccountSettings";
import MenuButton from "./MenuButton";
import useClasses from "../useClasses";
import { Box } from "@mui/material";

const styles = (theme) => ({
  root: {
    display: "flex",
    marginRight: DRAWER_WIDTH,
  },
  appBar: {
    background: theme.palette.background.toolbar,
    boxShadow: "none",
    [theme.breakpoints.up("sm")]: {
      borderRight: "1px solid rgba(0, 0, 0, 0.12)",
    },
    [theme.breakpoints.down("lg")]: {
      borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    },
  },
  appBarMobile: { top: "auto", bottom: 0, width: "100%" },
  appBarDesktop: {
    left: 0,
    height: "100%",
    width: DRAWER_WIDTH,
    justifyContent: "start",
  },
  toolbarDesktop: {
    padding: 0,
    height: "100%",
    display: "flex",
    justifyContent: "start",
    flexDirection: "column",
  },
  toolbarMobile: {
    padding: 0,
    width: "100%",
    display: "flex",
    justifyContent: "start",
    flexDirection: "row",
  },
  list: {},
  listDesktop: {
    padding: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  listMobile: {
    padding: 0,
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
  },
  avatar: {
    color: theme.palette.common.white,
    //borderRadius: theme.shape.borderRadius,
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  avatarSelected: { backgroundColor: theme.palette.primary.main },
  avaterUnselected: { backgroundColor: theme.palette.grey[500] },
});

function AccountIcon({ selected }) {
  const { accountSettings } = useAccountSettings();
  const classes = useClasses(styles);
  return (
    <Avatar
      className={clsx(classes.avatar, {
        [classes.avatarSelected]: selected,
        [classes.avaterUnselected]: !selected,
      })}
    >
      <Typography variant="body2">{`${accountSettings.firstName.charAt(
        0
      )}${accountSettings.lastName.charAt(0)}`}</Typography>
    </Avatar>
  );
}

export default function NavBar() {
  const classes = useClasses(styles);
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();
  const { app, dispatch } = useContext(AppContext);
  const location = useLocation();
  const mobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { accountSettings } = useAccountSettings();

  const homeButton = {
    id: "navBtnHome",
    title: app.project.id === 0 ? "m-Path" : `${app.project.firstName}`,
    icon:
      app.project.id === 0 ? (
        <HomeOutlinedIcon />
      ) : (
        <Avatar sx={{ bgcolor: stringToHslColor(app.project.code) }}>
          <FolderSharedOutlinedIcon />
        </Avatar>
      ),
    iconSelected:
      app.project.id === 0 ? (
        <HomeRoundedIcon />
      ) : (
        <Avatar sx={{ bgcolor: stringToHslColor(app.project.code) }}>
          <FolderSharedOutlinedIcon />
        </Avatar>
      ),
    paths: ["/home"],
    func: () => {
      history.push("/home");
      dispatch({ type: "SET_OPEN", payload: true }); // open the clients
    },
  };
  const navButtons = [
    {
      id: "navBtnClients",
      title:
        app.client.id > 0 ? app.client.alias : t("navigation.clients.title"),
      icon: <ContactsOutlinedIcon />,
      iconSelected: <ContactsRoundedIcon />,
      paths: [
        "/timeline",
        "/moodboards",
        "/surveys",
        "/scheduler",
        "/analyser",
        location.pathname === "/editor" &&
        typeof location.state?.interactionId === "string"
          ? "/editor"
          : "",
      ],
      func: () =>
        ["/timeline", "/moodboards", "/surveys"].includes(location.pathname)
          ? dispatch({ type: "TOGGLE_OPEN" })
          : history.push(app.path),
    },
    {
      id: "navBtnLibrary",
      title: t("navigation.lib"),
      icon: <CollectionsBookmarkOutlinedIcon />,
      iconSelected: <CollectionsBookmarkRoundedIcon />,
      paths: [
        "/interaction",
        "/library/public",
        "/library/local",
        "/library/contexts",
        "/library/contextId",
        location.pathname === "/editor" &&
        typeof location.state?.interactionId === "number"
          ? "/editor"
          : "",
      ],
      func: () =>
        history.push(
          accountSettings.disablePublicLibrary
            ? "/library/contexts"
            : app.libraryPath
        ),
    },
  ];
  const settingButton = {
    id: "settings",
    title: `@${accountSettings.code}`,
    icon: <AccountIcon selected={false} />,
    iconSelected: <AccountIcon selected={true} />,
    paths: ["/settings"],
    func: () => history.push("/settings"),
  };

  /*   const handleOnClick = useCallback(
    (path) => history.push(`/${path}`),
    [history]
  ); */

  // refresh button => goto start page
  useEffect(() => {
    // editor or scheduler refresh => goto local surveys
    if (
      app.client.id === -1 &&
      ["/editor", "/scheduler"].includes(location.pathname)
    )
      history.push("/surveys");
    // analyser refresh => goto timeline
    if (app.client.id === -1 && ["/analyser"].includes(location.pathname))
      history.push("/timeline");
  }, [app]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarDesktop]: !mobile,
          [classes.appBarMobile]: mobile,
        })}
      >
        <Toolbar
          className={clsx(classes.toolbar, {
            [classes.toolbarDesktop]: !mobile,
            [classes.toolbarMobile]: mobile,
          })}
        >
          <List
            className={clsx(classes.list, {
              [classes.listDesktop]: !mobile,
              [classes.listMobile]: mobile,
            })}
          >
            {mobile ? (
              [...navButtons, settingButton].map((btn) => (
                <MenuButton key={btn.id} {...btn} />
              ))
            ) : (
              <React.Fragment>
                <MenuButton {...homeButton} />
                <Box>
                  {navButtons.map((btn) => (
                    <MenuButton key={btn.id} {...btn} />
                  ))}
                </Box>
                <MenuButton {...settingButton} />{" "}
              </React.Fragment>
            )}
          </List>
        </Toolbar>
      </AppBar>
    </div>
  );
}
